import { generateCWRFile, EditionData, CWRSubmitterConfig } from './cwr_generator';
import {AuthorComposer, Edition, MusicalWork} from "../../../api/graphql";

// Hardcoded sample data (from the example in cwr_generator_ts)


let autorNumerator = 0;


function getPublishers(author: AuthorComposer) {
    const publishers = [];
    if (!author.publishers) {
        throw new Error(`Faltan las editoriales del autor "${author.firstName} ${author.lastName}"`);
    }
    for (const author_publisher of author.publishers) {
        const publisher = author_publisher.publisher;
        publishers.push({
            percentage: author_publisher.percentage,
            publisher: { name: publisher.name, ipi: publisher.caeIpi }
        })
    }
    return publishers;
}

function getAuthors(work: MusicalWork) {
    autorNumerator += 1;
    const authors = [];
    if (!work.authors) {
        throw new Error(`Faltan los autores de la obra "${work.title}"`);
    }
    for (const work_author of work.authors) {
        const author = work_author.author;
        if (!author.ipi) {
            throw new Error(`Falta el IPI del autor "${author.firstName} ${author.lastName}"`);
        }
        authors.push({
            id: `AGL${String(autorNumerator).padStart(4, "0")}`,
            author: {
                id: `A${String(autorNumerator).padStart(4, "0")}`,
                firstName: author.firstName,
                lastName: author.lastName,
                ipi: author.ipi,
                professionalInformation: {
                    managementSocieties: [{ id: "061" }], // Código para SADAIC
                    publishers: getPublishers(author)
                }
            },
            lyricsPercentage: work_author.lyricsPercentage,
            musicPercentage: work_author.musicPercentage
        });
    }
    return authors;
}

function getWorks(edition: Edition) {
    const works = [];
    for (const work of edition.works) {
        if (!work.iswc) {
            throw new Error(`Falta el ISWC de la obra "${work.title}"`);
        }
        if (!work.cmsId) {
            throw new Error(`Falta el ISGC de SADAIC de la obra "${work.title}"`);
        }
        works.push({
                id: `AGL${String(work.workNumber).padStart(14, "0")}`,
                title: work.title,
                iswc: work.iswc,
                musicalGenre: { id: "G1", name: "Pop" },
                territories: work.territories ? work.territories.map(x=> {return {name: x.name_en}}): [{ name: "Worldwide" }],
                cmsId: work.cmsId,
                notes: work.notes || "",
                authors: getAuthors(work)
            })
    }
    return works;
}

const sampleEditionData = (edition: Edition): EditionData => {
        return {
            id: edition.id,
            name: edition.name,
            publicationDate: "2024-01-15",
            works: getWorks(edition)
        };
}

const cwrConfig: Partial<CWRSubmitterConfig> = {
    senderId: 'TESTPUB',
    senderName: 'Test Publisher Co.',
    senderIpiNameNumber: '00099988800', // Example IPI for sender
    recipientId: '000', // Generic recipient
    recipientName: 'MULTIPLE RECIPIENTS'
};

/**
 * Generates a CWR filename based on the standard convention.
 * CWYYnnnnSUB_REP_VM-m-r.EXT
 * @param senderId - The CWR Sender ID of the submitter.
 * @param recipientId - The CWR Sender ID of the recipient.
 * @param sequence - A sequence number for the file.
 * @returns A string representing the CWR filename.
 */
export function generateCWRFilename(senderId: string, recipientId: string, sequence: number = 1): string {
    const now = new Date();
    const yearShort = String(now.getFullYear()).slice(-2); // YY
    const seqPadded = String(sequence).padStart(4, '0'); // nnnn
    const version = 'V3-1-0'; // CWR Version 3.1.0
    const extension = 'SUB'; // Submission file

    // Ensure senderId and recipientId are within typical CWR ID lengths (e.g., 2-9 chars, often 3-5)
    const sub = senderId.substring(0, 9);
    const rep = recipientId.substring(0, 9);

    return `CW${yearShort}${seqPadded}${sub}_${rep}_${version}.${extension}`;
}

/**
 * Triggers a browser download for the given text content.
 * @param filename - The desired name for the downloaded file.
 * @param content - The text content to be downloaded.
 */
function downloadTextFile(filename: string, content: string): void {
    if (typeof document === 'undefined') {
        console.log("Download function is intended for browser environments.");
        console.log("Filename:", filename);
        console.log("Content preview (first 500 chars):\n", content.substring(0,500));
        return;
    }

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=iso-8859-1,' + encodeURIComponent(content));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
    console.log(`Solicitando descarga del archivo: ${filename}`);
}

/**
 * Main function to generate and download the CWR file.
 */
export function createAndDownloadCWR(edition: Edition): void {
    console.log("Generando contenido del archivo CWR...");
    const cwrFileContent = generateCWRFile(sampleEditionData(edition), cwrConfig);

    if (cwrFileContent) {
        const filename = generateCWRFilename(cwrConfig.senderId || 'DEFSLT', cwrConfig.recipientId || '000', 1);
        console.log(`Contenido CWR generado. Preparando para descargar como: ${filename}`);
        downloadTextFile(filename, cwrFileContent);
    } else {
        console.error("No se pudo generar el contenido del archivo CWR.");
    }
}

// --- To make this runnable in a browser context, you might call it from an event listener ---
// Example: Attach to a button click
// Make sure this script is loaded as a module in your HTML if using imports,
// or bundle it with a tool like Webpack/Rollup.

// For direct execution in a browser console or simple HTML page:
// 1. Ensure cwr_generator_ts.js (compiled from .ts) is available.
// 2. You might need to expose createAndDownloadCWR globally or attach it to a button.

// Example of how to make it callable from HTML:
// (window as any).triggerCWRDownload = createAndDownloadCWR;
// Then in HTML: <button onclick="triggerCWRDownload()">Descargar Archivo CWR de Ejemplo</button>

// // If you are running this in a Node.js environment for testing (without a browser):
// if (typeof document === 'undefined') {
//     console.log("Ejecutando en un entorno sin navegador (por ejemplo, Node.js).");
//     console.log("La función de descarga no se ejecutará, pero el contenido CWR se generará.");
//     const cwrFileContent = generateCWRFile(sampleEditionData(edition), cwrConfig);
//     if (cwrFileContent) {
//         const filename = generateCWRFilename(cwrConfig.senderId || 'DEFSLT', cwrConfig.recipientId || '000', 1);
//         console.log(`\n--- Contenido CWR Generado para ${filename} ---`);
//         console.log(cwrFileContent);
//         console.log("--- Fin del Contenido CWR ---");
//     } else {
//         console.error("No se pudo generar el contenido del archivo CWR.");
//     }
// } else {
//     // If in a browser, you might want to automatically trigger it or provide a button.
//     // For this example, let's log a message and let the user call it via a button.
//     console.log("CWR Download script cargado. Puedes llamar a 'triggerCWRDownload()' o hacer clic en un botón configurado para ello.");
//      (window as any).triggerCWRDownload = createAndDownloadCWR; // Make it available globally for a button
// }

// You can also directly call it if you want the download to start when the script runs in a browser
// if (typeof document !== 'undefined') {
//   createAndDownloadCWR();
// }
